import {
  EuiButton,
  EuiComboBoxOptionOption,
  EuiFlexGroup,
  EuiFlexItem,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiStepsHorizontal,
  EuiToolTip,
  EuiSpacer,
  EuiIcon,
  EuiText,
  EuiPanel,
} from "@elastic/eui";
import { useMemo, useState } from "react";
import {
  AnketaQuestionnaireBase,
  DomainOption,
  ModalProps,
  OpsanioCreateContactDefaults,
} from "../types";
import { useAnketaI18n } from "src/internationalization";
import {
  InviteUser,
  KeycloakCreationRequest,
  KeycloakInvite,
  KeycloakOrganizations,
  KeycloakOrganizationUser,
} from "../../";
import { SelectOrCreateOrganization } from "./organisation";
import { SelectOrInviteMember } from "./members";
import { SelectQuestionnaire } from "./questionnaire";
import { SelectContacts } from "./contacts";
import { CreateQuestionnaireSummary } from "./summary";
import { QuestionnaireModal } from "./creation";
import { Catalog } from "anketa-core/types";
import NatureSelector from "./creation/vpc-name";

const EMPTY_CONTACTS: OpsanioCreateContactDefaults = {
  sales: [],
  technical: [],
  customerSales: [],
  customerTechnical: [],
  customerOrganizational: [],
};

interface QuestionnaireCreatorProps {
  newOrganizations: Array<EuiComboBoxOptionOption<KeycloakCreationRequest>>;
  newUsers: Array<EuiComboBoxOptionOption<InviteUser>>;
  members: Array<EuiComboBoxOptionOption<KeycloakOrganizationUser>>;
  organizations: Array<EuiComboBoxOptionOption<KeycloakOrganizations>>;
  selectedQuestionnaires: Array<
    EuiComboBoxOptionOption<AnketaQuestionnaireBase>
  >;
  displayName: string;
  updateCatalog: (c: Catalog) => void;
  vpcName: string;
  contacts: OpsanioCreateContactDefaults;
}

const QuestionnaireCreator = ({
  newOrganizations,
  newUsers,
  members,
  organizations,
  selectedQuestionnaires,
  displayName,
  updateCatalog,
  vpcName,
  contacts,
}: QuestionnaireCreatorProps): JSX.Element => {
  const newOrgAliases = newOrganizations.map((org) => org.value.alias);

  const organizationToCreate: Array<{
    organization: KeycloakCreationRequest;
    users: InviteUser[];
  }> = newOrganizations.map((newOrg) => ({
    organization: newOrg.value,
    users: newUsers
      .map((user) => user.value)
      .filter((user) => user.organizations === newOrg.value.alias),
  }));

  const unassignedNewUsers: Array<KeycloakInvite> = newUsers
    .map((user) => user.value)
    .filter((user) => !newOrgAliases.includes(user.organizations))
    .map((user) => {
      const usr: KeycloakInvite = {
        user: {
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
        },
        organizationId: organizations
          .map((org) => org.value)
          .find((org) => org.alias === user.organizations).id,
      };
      return usr;
    });

  const userToAssignToQuestionnaire = [
    ...members.map((member) => member.value.email),
    ...newUsers.map((user) => user.value.email),
  ];

  const catalog: AnketaQuestionnaireBase = {
    ...selectedQuestionnaires[0].value,
  };
  catalog.displayName = displayName;

  return (
    <QuestionnaireModal
      catalog={catalog}
      unassignedNewUsers={unassignedNewUsers}
      userToAssignToQuestionnaire={userToAssignToQuestionnaire}
      organizationToCreate={organizationToCreate}
      updateCatalog={updateCatalog}
      vpcName={vpcName}
      contacts={contacts}
    />
  );
};

export const CreateQuestionnaire = ({
  onClose,
  updateCatalog,
}: ModalProps): JSX.Element => {
  const { translate } = useAnketaI18n();

  const [currentStep, setCurrentStep] = useState<number>(0);

  const [organizations, setOrganizations] = useState<
    Array<EuiComboBoxOptionOption<KeycloakOrganizations>>
  >([]);

  const [newOrganizations, setNewOrganizations] = useState<
    Array<EuiComboBoxOptionOption<KeycloakCreationRequest>>
  >([]);

  const [newUsers, setNewUsers] = useState<
    Array<EuiComboBoxOptionOption<InviteUser>>
  >([]);

  const availableMembers = useMemo<
    Array<EuiComboBoxOptionOption<KeycloakOrganizationUser>>
  >(() => {
    return organizations
      .map((organization) =>
        Array.isArray(organization.value.members)
          ? organization.value.members.map((member) => ({
              label: member.email,
              value: member,
              color: member.emailVerified ? "success" : "danger",
            }))
          : [],
      )
      .flat();
  }, [organizations]);

  const [members, setMembers] = useState<
    Array<EuiComboBoxOptionOption<KeycloakOrganizationUser>>
  >([]);

  const domains = useMemo<Array<DomainOption>>(() => {
    const oldDomain = organizations.flatMap((org) => {
      const organizationAlias = org.value.alias;
      return org.value.domains.map((v) => ({
        domain: `@${v.name}`,
        organizationAlias,
      }));
    });
    const newDomain = newOrganizations.flatMap((org) => {
      const organizationAlias = org.value.alias;
      return org.value.domains.map((v) => ({
        domain: `@${v}`,
        organizationAlias,
      }));
    });
    return [...newDomain, ...oldDomain];
  }, [organizations, newOrganizations]);

  const [selectedQuestionnaires, setSelectedQuestionnaires] = useState<
    Array<EuiComboBoxOptionOption<AnketaQuestionnaireBase>>
  >([]);
  const [displayName, setDisplayName] = useState<string>("");

  // Contacts captured in the wizard and seeded into the General section at
  // creation time. Optional - the step never blocks progress (customer
  // contacts in particular may be left empty and filled in later).
  const [contacts, setContacts] =
    useState<OpsanioCreateContactDefaults>(EMPTY_CONTACTS);

  const [creationStarted, setCreationStarted] = useState(false);

  const [hasDomainConflicts, setHasDomainConflicts] = useState(false);

  const isQuestionnaireReady =
    displayName.trim() !== "" && selectedQuestionnaires.length === 1;

  const [owner, setOwner] = useState<string | null>(null);
  const [stage, setStage] = useState<string | null>(null);

  const isVpcReady = owner !== null && stage !== null;

  const vpcName = useMemo<string>(() => {
    return owner && stage ? `opsanio-${owner}-${stage}` : "";
  }, [owner, stage]);

  const steps = [
    {
      title: translate("anketa.organization"),
      isComplete:
        (organizations.flatMap((org) => org.value?.domains ?? []).length > 0 ||
          newOrganizations.flatMap((org) => org.value?.domains ?? []).length >
            0) &&
        !hasDomainConflicts,
      body: (
        <SelectOrCreateOrganization
          organizations={organizations}
          setOrganizations={setOrganizations}
          setNewOrganizations={setNewOrganizations}
          newOrganizations={newOrganizations}
          onConflictChange={setHasDomainConflicts}
        />
      ),
    },
    {
      title: translate("anketa.members"),
      isComplete: members.length > 0 || newUsers.length > 0,
      body: (
        <SelectOrInviteMember
          setMembers={setMembers}
          members={members && members.length > 0 ? members : []}
          availableMembers={availableMembers}
          setNewUsers={setNewUsers}
          newUsers={newUsers}
          domains={domains}
        />
      ),
    },
    {
      title: translate("anketa.questionnaire"),
      isComplete: isQuestionnaireReady,
      body: (
        <SelectQuestionnaire
          selectedQuestionnaires={selectedQuestionnaires}
          setSelectedQuestionnaires={setSelectedQuestionnaires}
          displayName={displayName}
          setDisplayName={setDisplayName}
        />
      ),
    },
    {
      title:
        translate("anketa.contacts", { defaultMessage: "Contacts" }) ||
        "Contacts",
      // Optional step: contacts (especially the customer ones) may be left
      // empty and completed later, so it never blocks the wizard.
      isComplete: true,
      body: <SelectContacts contacts={contacts} setContacts={setContacts} />,
    },
    {
      title: "VPC name",
      isComplete: isVpcReady,
      body: (
        <>
          <NatureSelector
            onAnimalChange={(value: string | null) => setOwner(value)}
            onPlantChange={(value: string | null) => setStage(value)}
          />
          {isVpcReady && (
            <>
              <EuiSpacer size="l" />
              <EuiPanel
                color="subdued"
                borderRadius="m"
                paddingSize="m"
                style={{
                  borderLeft: "3px solid #00BFB3",
                  transition: "all 0.2s ease",
                }}
              >
                <EuiFlexGroup
                  alignItems="center"
                  gutterSize="s"
                  responsive={false}
                >
                  <EuiFlexItem grow={false}>
                    <EuiIcon type="checkInCircleFilled" color="success" />
                  </EuiFlexItem>
                  <EuiFlexItem>
                    <EuiText size="s">
                      <strong>{vpcName}</strong>
                    </EuiText>
                  </EuiFlexItem>
                </EuiFlexGroup>
              </EuiPanel>
            </>
          )}
        </>
      ),
    },
    {
      title: translate("anketa.summary"),
      isComplete: isQuestionnaireReady,
      body: (
        <CreateQuestionnaireSummary
          selectedQuestionnaires={selectedQuestionnaires}
          displayName={displayName}
          newUsers={newUsers}
          members={members}
          newOrganizations={newOrganizations}
          organizations={organizations}
        />
      ),
    },
    {
      title: translate("anketa.create.action", {
        values: { type: translate("anketa.questionnaire") },
      }),
      isComplete: isQuestionnaireReady,
      body: creationStarted ? (
        <QuestionnaireCreator
          newOrganizations={newOrganizations}
          newUsers={newUsers}
          members={members}
          organizations={organizations}
          selectedQuestionnaires={selectedQuestionnaires}
          displayName={displayName}
          updateCatalog={updateCatalog}
          vpcName={vpcName}
          contacts={contacts}
        />
      ) : null,
    },
  ];

  const horizontalSteps = steps.map((step, index) => ({
    title: step.title,
    isComplete: step.isComplete && currentStep > index,
    isSelected: currentStep === index,
    onClick: () => {
      if (creationStarted && index === steps.length - 1) return;
      if (!creationStarted || index < steps.length - 1) {
        setCurrentStep(index);
      }
    },
  }));

  const canGoNext = useMemo(
    () => steps[currentStep].isComplete,
    [currentStep, steps],
  );

  const isLastStep = currentStep === steps.length - 1;
  const isSummaryStep = currentStep === steps.length - 2;

  const goNext = () => {
    if (isLastStep) {
      onClose();
      return;
    }
    if (!canGoNext) return;
    if (isSummaryStep) {
      setCreationStarted(true);
    }
    setCurrentStep((prev) => prev + 1);
  };

  const goBack = () => {
    if (currentStep > 0 && !creationStarted) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const nextButtonLabel = isLastStep
    ? translate("anketa.action.close")
    : isSummaryStep
      ? translate("anketa.create.action.create")
      : translate("anketa.create.action.next");

  return (
    <EuiModal
      onClose={onClose}
      // Responsive: cap the width but never force a min that overflows phones
      // (the previous minWidth:640 caused horizontal scroll at 375px).
      style={{ maxWidth: 860, width: "100%" }}
    >
      <EuiModalHeader>
        <EuiFlexGroup direction="column" gutterSize="m">
          <EuiFlexItem>
            <EuiModalHeaderTitle>
              <h1>
                {translate("anketa.create", {
                  values: { type: translate("anketa.questionnaire") },
                })}
              </h1>
            </EuiModalHeaderTitle>
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiStepsHorizontal steps={horizontalSteps} />
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiModalHeader>

      <EuiModalBody>
        <EuiSpacer size="s" />
        {steps[currentStep].body}
        <EuiSpacer size="s" />
      </EuiModalBody>

      <EuiModalFooter>
        <EuiFlexGroup justifyContent="spaceBetween" alignItems="center">
          <EuiFlexItem grow={false}>
            <EuiButton
              onClick={goBack}
              disabled={currentStep === 0 || creationStarted}
              iconType="arrowLeft"
            >
              {translate("anketa.create.action.back")}
            </EuiButton>
          </EuiFlexItem>

          <EuiFlexItem grow={false}>
            <EuiToolTip
              content={
                !canGoNext && !isLastStep
                  ? translate("anketa.create.action.next.disabled.hint") ||
                    "Complete all required fields to continue"
                  : undefined
              }
            >
              <EuiButton
                onClick={goNext}
                disabled={!canGoNext && !isLastStep}
                iconType={
                  isLastStep ? "cross" : isSummaryStep ? "check" : "arrowRight"
                }
                iconSide="right"
                fill
                color={isSummaryStep ? "success" : "primary"}
              >
                {nextButtonLabel}
              </EuiButton>
            </EuiToolTip>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiModalFooter>
    </EuiModal>
  );
};
